import { Facebook, Instagram, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { NewsletterForm } from "./NewsletterForm";
import { SITE } from "../config/site";
import { scrollToId } from "../lib/scroll";
import { track } from "../lib/analytics";
import { useBookingUrl, useContent } from "../context/ContentContext";

const SubstackIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
  </svg>
);

const EXPLORE = [
  { label: "Wellness Discovery", to: "/wellness-discovery", id: "wellness-discovery" },
  { label: "Terrain and Truth", to: "/terrain-and-truth", id: "terrain-and-truth" },
  { label: "The Detox Gospel", to: "/the-detox-gospel", id: "the-detox-gospel" },
  { label: "Staci's Favorites", to: "/favorites", id: "favorites" },
  { label: "About", hash: "#meet-staci", id: "about" },
  { label: "Approach", hash: "#approach", id: "approach" },
  { label: "Consultations", hash: "#consultations", id: "consultations" },
  { label: "How It Works", hash: "#how-it-works", id: "how-it-works" },
  { label: "FAQs", hash: "#faq", id: "faqs" },
];

export const Footer = () => {
  const bookingUrl = useBookingUrl();
  const footerEmail = useContent("footer_email", "Email: to be added");
  const socials = [
    { id: "instagram", icon: Instagram, url: SITE.social.instagram },
    { id: "substack", icon: SubstackIcon, url: SITE.social.substack },
    { id: "facebook", icon: Facebook, url: SITE.social.facebook },
    { id: "youtube", icon: Youtube, url: SITE.social.youtube },
  ].filter((s) => s.url);

  return (
    <footer id="contact" data-testid="main-footer" className="bg-forest text-ivory">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo dark />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-ivory/60">
              Holistic wellness and spiritual education for women and mothers: warm, honest,
              and rooted in real life.
            </p>
            <div className="mt-7 flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.id}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${s.id}`}
                  onClick={() => track("social_click", { platform: s.id, location: "footer" })}
                  aria-label={`Rooted Healing on ${s.id}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-ivory/20 text-ivory/75 transition-all duration-300 hover:border-gold-soft hover:text-gold-soft"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
          <nav className="lg:col-span-2" aria-label="Footer">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-soft">Explore</h3>
            <ul className="mt-5 flex flex-col gap-3">
              {EXPLORE.map((l) => (
                <li key={l.id}>
                  {l.to ? (
                    <Link
                      to={l.to}
                      data-testid={`footer-nav-${l.id}`}
                      className="text-sm text-ivory/70 transition-colors duration-300 hover:text-gold-soft"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      href={l.hash}
                      data-testid={`footer-nav-${l.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToId(l.hash);
                      }}
                      className="text-sm text-ivory/70 transition-colors duration-300 hover:text-gold-soft"
                    >
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
          <div className="lg:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-soft">Contact</h3>
            <ul className="mt-5 flex flex-col gap-3 text-sm text-ivory/70">
              <li data-testid="footer-contact-email">{footerEmail}</li>
              <li>{SITE.contact.note}</li>
              <li>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-booking-link"
                  onClick={() => track("booking_click", { location: "footer" })}
                  className="text-gold-soft underline-offset-4 transition-colors duration-300 hover:underline"
                >
                  Book a Session
                </a>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-4">
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-soft">
              The Letter
            </h3>
            <p className="mt-5 text-sm leading-relaxed text-ivory/60">
              Encouragement, insights, and resources, straight to your inbox.
            </p>
            <div className="mt-5">
              <NewsletterForm source="footer" compact dark />
            </div>
          </div>
        </div>
        <div className="mt-16 flex flex-col items-start justify-between gap-5 border-t border-ivory/12 pt-8 text-xs text-ivory/50 sm:flex-row sm:items-center">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} Rooted Healing by Staci Freed. All rights reserved.
          </p>
          <p className="max-w-md leading-relaxed">
            Educational wellness guidance only, not a substitute for medical advice, diagnosis,
            or treatment.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="#privacy"
              data-testid="footer-privacy-link"
              onClick={(e) => e.preventDefault()}
              className="transition-colors duration-300 hover:text-gold-soft"
            >
              Privacy Policy
            </a>
            <a
              href="#terms"
              data-testid="footer-terms-link"
              onClick={(e) => e.preventDefault()}
              className="transition-colors duration-300 hover:text-gold-soft"
            >
              Terms of Use
            </a>
            <Link
              to="/login"
              state={{ from: "/admin" }}
              data-testid="footer-admin-link"
              className="transition-colors duration-300 hover:text-gold-soft"
            >
              Admin Login
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
